import { useLocalization, createLazyResource, createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createEffect, createSignal, on } from 'solid-js';
import { confirmDeleteBill } from '~/components/bills/ConfirmDeleteBill';
import { createMagicDoorContext } from '~/contexts/utils';
import { BillRepository } from '~/repositories/billRepository';
import { diffHydrateFiles } from '~/utils/uploaderAdapter';
import { syncBillFileChanges } from './billFileSync';
import type { HydrateFile } from '@magicdoorinc/ui';
import type { AddBillFilePayload } from '~/repositories/billRepository';
import type { CreateVendorBillRequestDto, GetVendorBillsParams, UpdateVendorBillRequestDto, VendorBillDto } from '~/swagger/accounting/Api';
import type { EstimateVendorBillAchArrivalTimesDto } from '~/swagger/portal/Api';

const repo = new BillRepository();

export const [BillsProvider, useBills] = createMagicDoorContext('Bills', () => {
  const { t } = useLocalization();
  const [filter, setFilter] = createSignal<GetVendorBillsParams>({});

  const [bills, getBills, billsActions] = createTriggerResource(async (filters: GetVendorBillsParams) => {
    const pagination = await repo.getBills(filters);
    return {
      items: pagination.items,
      page: pagination.currentPage,
      pageSize: pagination.pageSize,
      totalPages: pagination.totalPages,
      totalCount: pagination.totalCount,
      chartOfAccounts: pagination.chartOfAccounts || [],
      properties: pagination.properties || [],
      units: pagination.units || [],
      vendors: pagination.vendors || [],
    };
  });

  createEffect(
    on(
      filter,
      (f) => {
        getBills(f);
      },
      { defer: true }
    )
  );

  const [fileList, setFileList] = createSignal<HydrateFile[]>([]);
  const [currentId, setCurrentId] = createSignal<string | undefined>(undefined);
  const [current, currentActions] = createLazyResource(currentId, (id) => repo.getBill(id));

  const syncBillFiles = async (billId: string) => {
    await syncBillFileChanges(repo, billId, fileList());
  };

  const addBill = createMutation(async (model: CreateVendorBillRequestDto, callback?: any) => {
    const created = await repo.createBill(model);
    getBills(filter());

    const { newFiles } = diffHydrateFiles(fileList());
    newFiles.forEach((file) => {
      callback?.(`${t('Uploading')}  ${file.name}...`);
    });

    if (newFiles.length > 0) {
      await syncBillFiles(created.id);
      fileList().forEach((file) => file.rawFile != null && file.idOrUrl.startsWith('blob:') && URL.revokeObjectURL(file.idOrUrl));
    }

    return created;
  });

  const updateBill = createMutation(async (billId: string, model: UpdateVendorBillRequestDto): Promise<VendorBillDto> => {
    const updated = await repo.updateBill(billId, model);
    await syncBillFiles(billId);
    getBills(filter());
    currentActions.refetch();
    return updated;
  });

  const setBillSource = createMutation(async (billId: string, workOrderId: string | undefined) => {
    const updated = await repo.setBillSource(billId, workOrderId);
    currentActions.refetch();
    getBills(filter());
    return updated;
  });

  const deleteBill = createMutation(async (billId: string) => {
    await confirmDeleteBill(t, async () => {
      await repo.deleteBill(billId);
    });
  });

  const deleteBillItem = createMutation(async (billId: string, paymentId: string) => {
    await confirmDeleteBill(t, async () => {
      await repo.deleteBillItem(billId, paymentId);
      currentActions.refetch();
    });
  });

  const addBillFile = createMutation(async (billId: string, files: AddBillFilePayload[]) => {
    await repo.addBillFile(billId, files);
    currentActions.refetch();
  });

  const deleteBillFiles = createMutation(async (billId: string, fileIds: string[]) => {
    await repo.deleteBillFiles(billId, fileIds);
    currentActions.refetch();
  });

  const estimateVendorBillAchArrivalTimes = createMutation(async (payload: EstimateVendorBillAchArrivalTimesDto) => {
    const result = await repo.estimateVendorBillAchArrivalTimes(payload);
    return result;
  });

  return {
    get filtered() {
      return bills;
    },
    loadBills() {
      getBills(filter());
    },
    refetch() {
      billsActions.refetch();
    },
    setFilter,
    setCurrentId,
    current,
    bills,
    addBill,
    updateBill,
    setBillSource,
    deleteBill,
    deleteBillItem,
    addBillFile,
    deleteBillFiles,
    setFileList,
    fileList,
    estimateVendorBillAchArrivalTimes,
  };
});
